import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import MetricTable from "./components/MetricTable"
import MetricDetail from "./components/MetricDetail"
import DeleteConfirmDialog from "./components/DeleteConfirmDialog"
import CannotDeleteDialog from "./components/CannotDeleteDialog"
import BasicButton from "@/components/button/BasicButton"
import {
  useCalculatedMetricsQuery,
  useCreateCalculatedMetricsMutation,
  useUpdateCalculatedMetricsMutation,
  useDeleteCalculatedMetricsMutation,
} from "@/hooks/api/useSettingQuery"
import { CalculatedMetric } from "@/types/setting"
import { generateDefaultName } from "./utils/defaultNameGenerator"
import { createGlobalStore } from "@/stores/global"

import "./calculator.scss"
import Icon from "@/components/icon/Icon"

export default function CalculatorPage() {
  const { t } = useTranslation()
  const { data: metricsData, isLoading } = useCalculatedMetricsQuery()
  const setSnackBar = createGlobalStore((state) => state.setSnackBar)

  const createMutation = useCreateCalculatedMetricsMutation()
  const updateMutation = useUpdateCalculatedMetricsMutation()
  const deleteMutation = useDeleteCalculatedMetricsMutation()

  const [selectedMetricId, setSelectedMetricId] = useState<number | null>(null)
  const [editingMetric, setEditingMetric] = useState<CalculatedMetric | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [cannotDeleteDialogOpen, setCannotDeleteDialogOpen] = useState(false)

  const metricList = metricsData ?? []

  // 테이블에 표시할 데이터 (새 메트릭 생성 중이면 맨 아래에 추가)
  const tableData = editingMetric?.idfMetric === null ? [...metricList, editingMetric] : metricList

  const canAdd = metricList.length < 30

  // 초기 로드 완료 여부 (한 번만 자동 선택)
  const isInitialized = useRef(false)

  // 초기 로드 시 첫 번째 메트릭 자동 선택
  useEffect(() => {
    if (!isInitialized.current && metricList.length > 0) {
      const firstMetric = metricList[0]
      setSelectedMetricId(firstMetric.idfMetric)
      setEditingMetric({ ...firstMetric })
      isInitialized.current = true
    }
  }, [metricList])

  // 메트릭 선택
  const handleSelect = (metric: CalculatedMetric) => {
    setSelectedMetricId(metric.idfMetric)
    setEditingMetric({ ...metric })
  }

  // 새로 만들기
  const handleAdd = () => {
    const defaultName = generateDefaultName(metricList.map((m: CalculatedMetric) => m.name))
    const newMetric: CalculatedMetric = {
      idfMetric: null,
      key: null,
      name: defaultName,
      formula: "",
    }
    setSelectedMetricId(null)
    setEditingMetric(newMetric)
  }

  // 저장
  const handleSave = async () => {
    if (!editingMetric) return

    try {
      if (editingMetric.idfMetric === null) {
        const result = await createMutation.mutateAsync(editingMetric)
        if (result.response) {
          setSelectedMetricId(result.response.idfMetric)
          setEditingMetric(result.response)
        }
      } else {
        await updateMutation.mutateAsync(editingMetric)
      }
      setSnackBar({ message: t("messages.successUpdate"), severity: "success", open: true })
    } catch {
      setSnackBar({ message: t("messages.failBasic"), severity: "error", open: true })
    }
  }

  // 삭제
  const handleDelete = async () => {
    if (!selectedMetricId) return

    try {
      await deleteMutation.mutateAsync(selectedMetricId)
      setDeleteDialogOpen(false)

      // 삭제 후 남은 메트릭 중 첫 번째 선택
      const remaining = metricList.filter((m) => m.idfMetric !== selectedMetricId)
      if (remaining.length > 0) {
        setSelectedMetricId(remaining[0].idfMetric)
        setEditingMetric({ ...remaining[0] })
      } else {
        setSelectedMetricId(null)
        setEditingMetric(null)
      }
      setSnackBar({ message: t("messages.successDelete"), severity: "success", open: true })
    } catch {
      setSnackBar({ message: t("messages.failBasic"), severity: "error", open: true })
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending
  const isNew = editingMetric?.idfMetric === null

  return (
    <div className="CalculatorPageCont">
      <div className="calculator-header">
        <h3 className="settings-section-title">{t("customMetricCalculator")}</h3>
        <BasicButton type="default-outlined" size="md" onClick={handleAdd} disabled={!canAdd}>
          <Icon type="add" size={16} />
          {t("newCustomMetric")}
        </BasicButton>
      </div>

      <div
        className="CalculatorTableCont"
        style={!editingMetric ? { gridTemplateColumns: "1fr" } : undefined}>
        <MetricTable
          data={tableData}
          selectedId={isNew ? (editingMetric?.name ?? null) : selectedMetricId}
          onSelect={handleSelect}
          isLoading={isLoading}
        />

        {editingMetric && (
          <MetricDetail
            metric={editingMetric}
            onChange={setEditingMetric}
            onSave={handleSave}
            onDelete={() => {
              const isInUse =
                editingMetric.includedTemplates && editingMetric.includedTemplates.length > 0
              if (isInUse) {
                setCannotDeleteDialogOpen(true)
              } else {
                setDeleteDialogOpen(true)
              }
            }}
            isSaving={isSaving}
            isNew={isNew}
            existingNames={metricList
              .filter((m: CalculatedMetric) => m.idfMetric !== editingMetric.idfMetric)
              .map((m: CalculatedMetric) => m.name)}
          />
        )}
      </div>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialogOpen(false)}
        isDeleting={deleteMutation.isPending}
      />

      <CannotDeleteDialog
        open={cannotDeleteDialogOpen}
        onClose={() => setCannotDeleteDialogOpen(false)}
        templates={editingMetric?.includedTemplates ?? []}
      />
    </div>
  )
}
